import type { Component, JSXElement } from "solid-js";
import { createContext, useContext } from "solid-js";
import { FatalStartupErrorScreen } from "./components/FatalStartupErrorScreen";
import { defaultWire, type Wire } from "./definition";
import { newWire, validateWire } from "./factory";

const WireContext = createContext(defaultWire);
const getGlobalWire = (): Wire | undefined => (globalThis as { stMountedWire?: Wire }).stMountedWire;

// useWire provides access to dependencies, global state, and the ability to
// interact with domains/business-logic. This function must be called from
// within a SolidJS context established by the application.
export const useWire = (): Wire => {
  const wire = useContext(WireContext);
  if (!wire) {
    throw new Error("useWire must be used within a Store.Provider");
  }
  // createContext(defaultWire) is truthy when no Provider matched (e.g. duplicate wire module / wrong tree).
  if (!wire.wireInitialized) {
    const globalWire = getGlobalWire();
    if (globalWire?.wireInitialized) {
      return globalWire;
    }
    throw new Error(
      "useWire: no WireProvider above this component. Reload the page. If this persists, the app bundle may have duplicated the wire module and broken Solid context."
    );
  }
  return wire;
};

// WireProvider provides access to dependencies, global state, and the ability
// to interact with domains/business-logic.
export const WireProvider: Component<{
  children: JSXElement;
}> = (props) => {
  const wire = newWire();
  (globalThis as { stMountedWire?: Wire }).stMountedWire = wire;
  try {
    validateWire(wire);
  } catch (e: unknown) {
    return FatalStartupErrorScreen(e);
  }
  return <WireContext.Provider value={wire}>{props.children}</WireContext.Provider>;
};
